import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Comment, CommentCreateRequest, CommentUpdateRequest, CommentType } from '../types/api';
import { commentsNew } from '../services/api';
import { useCacheConfig } from '../contexts/cache-config-context';

export const useAllComments = () => {
  const { getCacheTime } = useCacheConfig();
  
  return useQuery({
    queryKey: ['comments'],
    queryFn: () => commentsNew.getAllComments(),
    staleTime: getCacheTime('comments'),
  });
};

export const useComment = (id: string) => {
  const { getCacheTime } = useCacheConfig();
  
  return useQuery({
    queryKey: ['comments', id],
    queryFn: () => commentsNew.getCommentById(id),
    staleTime: getCacheTime('comments'),
    enabled: !!id,
  });
};

export const useCommentsByRequest = (requestId: string) => {
  const { getCacheTime } = useCacheConfig();
  
  return useQuery({
    queryKey: ['comments', 'request', requestId],
    queryFn: () => commentsNew.getCommentsByRequest(requestId),
    staleTime: getCacheTime('comments'),
    enabled: !!requestId,
  });
};

export const useCommentsByTask = (taskId: string) => {
  const { getCacheTime } = useCacheConfig();
  
  return useQuery({
    queryKey: ['comments', 'task', taskId],
    queryFn: () => commentsNew.getCommentsByTask(taskId),
    staleTime: getCacheTime('comments'),
    enabled: !!taskId,
  });
};

export const useCommentsByUser = (userId: string) => {
  const { getCacheTime } = useCacheConfig();
  
  return useQuery({
    queryKey: ['comments', 'user', userId],
    queryFn: () => commentsNew.getCommentsByUser(userId),
    staleTime: getCacheTime('comments'),
    enabled: !!userId,
  });
};

export const useRecentComments = (limit: number = 10) => {
  const { getCacheTime } = useCacheConfig();
  
  return useQuery({
    queryKey: ['comments', 'recent', limit],
    queryFn: () => commentsNew.getRecentComments(limit),
    staleTime: getCacheTime('comments'),
  });
};

export const useSearchComments = (searchTerm: string) => {
  const { getCacheTime } = useCacheConfig();
  
  return useQuery({
    queryKey: ['comments', 'search', searchTerm],
    queryFn: () => commentsNew.searchComments({ searchTerm }),
    staleTime: getCacheTime('comments'),
    enabled: !!searchTerm && searchTerm.length >= 2,
  });
};

export const useCreateComment = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (newComment: CommentCreateRequest) => commentsNew.createComment(newComment),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comments'] });
    },
  });
};

export const useUpdateComment = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, comment }: { id: string; comment: CommentUpdateRequest }) =>
      commentsNew.updateComment(id, comment),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['comments'] });
      queryClient.invalidateQueries({ queryKey: ['comments', variables.id] });
    },
  });
};

export const useDeleteComment = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => commentsNew.deleteComment(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comments'] });
    },
  });
};

export const useMarkCommentAsResolved = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => commentsNew.toggleCommentVisibility(id), // Using visibility toggle instead
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comments'] });
    },
  });
};

export const useReactToComment = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, reaction }: { id: string; reaction: string }) =>
      // Not available in new service - keeping as placeholder
      Promise.resolve({ id, reaction }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comments'] });
    },
  });
};

export const useReportComment = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      // Not available in new service - keeping as placeholder
      Promise.resolve({ id, reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comments'] });
    },
  });
};

export const useReplyToComment = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ parentId, content }: { parentId: string; content: string }) =>
      commentsNew.createComment({ 
        content, 
        visibility: 1, // Default visibility
        parentCommentId: parentId 
      } as CommentCreateRequest),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comments'] });
    },
  });
};

// Bulk delete not available in new service
export const useBulkDeleteComments = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (ids: string[]) => 
      Promise.all(ids.map(id => commentsNew.deleteComment(id))),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comments'] });
    },
  });
};

export const useModerateComment = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'approve' | 'reject' | 'hide' }) =>
      // Use toggle visibility as a substitute for moderation
      commentsNew.toggleCommentVisibility(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comments'] });
    },
  });
};

// Hook aggiuntivi per le funzionalità specifiche del CommentService
// Note: Many methods don't exist in new service, keeping as placeholders or removing

// Removed: useCommentReplies - not available in new service
// Removed: useCommentThread - not available in new service
// Removed: useUnreadComments - not available in new service
// Removed: useUnreadCommentsCount - not available in new service
// Removed: useMarkCommentAsRead - not available in new service
// Removed: useMarkCommentAsUnread - not available in new service
// Removed: useMentionUser - not available in new service
// Removed: useCommentMentions - not available in new service

export const useCommentStats = (id: string) => {
  const { getCacheTime } = useCacheConfig();
  
  return useQuery({
    queryKey: ['comments', id, 'stats'],
    queryFn: () => commentsNew.getCommentStatistics(),
    staleTime: getCacheTime('comments'),
    enabled: !!id,
  });
};
